import { ArenaSupportTopicSchema } from "./arena-support-chat.dto";
import { z } from "zod";

/** Rerank scale: 3 fully answers, 2 answers (maybe partially), 1 related only, 0 unrelated. */
export const ARENA_SUPPORT_AGENT_RELEVANCE_FLOOR = 2;
/** A related entry is no fix for a bug: a bug report gets a KB answer only from one that fully answers it. */
export const ARENA_SUPPORT_AGENT_BUG_REPORT_RELEVANCE_FLOOR = 3;

/** Each search (vector, keyword) returns this many entries. */
export const ARENA_SUPPORT_AGENT_SEARCH_TOP_K = 20;
/** Reciprocal Rank Fusion constant: score = Σ 1 / (k + rank). */
export const ARENA_SUPPORT_AGENT_RRF_K = 60;

/** The question and each alternate phrasing; enforced on the entry files by `pnpm kb:check`. */
export const ARENA_SUPPORT_KB_TEXT_MAX_LENGTH = 500;
export const ARENA_SUPPORT_KB_PHRASINGS_MAX_COUNT = 20;
export const ARENA_SUPPORT_KB_ANSWER_MAX_LENGTH = 4000;
export const ARENA_SUPPORT_GOLDEN_EXPECTED_ENTRIES_MAX = 10;

export const ArenaSupportKbEmbeddingStatusSchema = z.enum(["PENDING", "READY", "FAILED"]);
export type ArenaSupportKbEmbeddingStatus = z.infer<typeof ArenaSupportKbEmbeddingStatusSchema>;

export const ArenaSupportKbEntrySchema = z.object({
  /** The entry file's slug in `apps/api/prisma-aws/arena-support-kb/entries`. */
  id: z.string(),
  topic: ArenaSupportTopicSchema,
  question: z.string(),
  alternatePhrasings: z.array(z.string()),
  answer: z.string(),
  /** Retrieval only sees entries that are `aiEnabled`, `READY` and not hidden by a feature flag. */
  aiEnabled: z.boolean(),
  /** Live only while every one of these feature flags is on. */
  requiresFlags: z.array(z.string()),
  /** Hidden while any of these feature flags is on. */
  hiddenWhenFlags: z.array(z.string()),
  /** The flags above hide the entry right now. */
  hiddenByFlags: z.boolean(),
  embeddingStatus: ArenaSupportKbEmbeddingStatusSchema,
  embeddingError: z.string().nullable(),
  /** Bumped by every KB sync that changes the entry; the embedding job is keyed on it. */
  revision: z.number().int(),
  updatedBy: z.string().nullable(),
  updatedAt: z.number(),
});
export type ArenaSupportKbEntryDto = z.infer<typeof ArenaSupportKbEntrySchema>;

const kbText = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .refine((value) => value.trim().length > 0, { message: "Cannot be blank" });

/** Query for `GET /v1/cms/arena-support-agent/kb`. */
export const CmsListArenaSupportKbEntriesQuerySchema = z.object({
  topic: ArenaSupportTopicSchema.optional(),
});

export const CmsArenaSupportKbEntriesResponseSchema = z.object({ entries: z.array(ArenaSupportKbEntrySchema) });
export type CmsArenaSupportKbEntriesResponse = z.infer<typeof CmsArenaSupportKbEntriesResponseSchema>;

/** Body for `POST /v1/cms/arena-support-agent/kb/retry-embedding`. */
export const CmsArenaSupportKbEntryIdRequestSchema = z.object({ id: z.string().min(1) });

// ─── Pipeline trace (shared by runs and the playground) ──────────────────────

export const ArenaSupportAgentRunModeSchema = z.enum(["LIVE", "SHADOW"]);
export type ArenaSupportAgentRunMode = z.infer<typeof ArenaSupportAgentRunModeSchema>;

export const ArenaSupportAgentOutcomeSchema = z.enum([
  /** Floor met + verification passed: answer posted (or stored as draft in SHADOW). */
  "CONFIDENT",
  /** First low-confidence run in a row: user asked to rephrase. */
  "LOW_CONFIDENCE_REPHRASE",
  /** Second low-confidence run in a row: ack, team requested; the AI keeps answering other topics. */
  "LOW_CONFIDENCE_HANDOFF",
  /** Pipeline could not run: ack + conversation handed to HUMAN. */
  "FAILURE",
  /** Ops took over (or the conversation closed) before the run could answer. Nothing sent. */
  "SKIPPED",
  /** The message asked nothing: greeted back, streak unchanged. */
  "GREETING",
  /** Thanks or an acknowledgement of a previous reply: "you're welcome", streak unchanged. */
  "THANKS",
  /** Product feedback: thanked, ops pinged, streak unchanged. */
  "FEEDBACK",
  /** A problem the KB could not answer: ack, team requested; the AI keeps answering other topics. */
  "BUG_REPORT_HANDOFF",
  /** The team was already requested and the user wrote about that problem again: wait note once, then nothing. */
  "WAITING_FOR_TEAM",
  /** A problem the KB could not answer: the AI asked for details before reporting it. */
  "DETAILS_REQUESTED",
]);
export type ArenaSupportAgentOutcome = z.infer<typeof ArenaSupportAgentOutcomeSchema>;

export const ArenaSupportAgentFailureReasonSchema = z.enum([
  "OPENAI_ERROR",
  "TIMEOUT",
  "BUDGET_EXHAUSTED",
  "NO_READY_ENTRIES",
  "INVALID_MODEL_OUTPUT",
  "MISSING_CONFIG",
  /** Anything else that stopped the pipeline (database, Redis, unexpected error); see Sentry. */
  "INTERNAL_ERROR",
]);
export type ArenaSupportAgentFailureReason = z.infer<typeof ArenaSupportAgentFailureReasonSchema>;

/** What the latest user message is, read by the rewrite step with the conversation. */
export const ArenaSupportAgentIntentSchema = z.enum(["QUESTION", "BUG_REPORT", "FEEDBACK", "GREETING", "THANKS"]);
export type ArenaSupportAgentIntent = z.infer<typeof ArenaSupportAgentIntentSchema>;

/** Rerank score an entry needs to be given to the answer step. */
export function arenaSupportAgentRelevanceFloor(intent: ArenaSupportAgentIntent | null): number {
  return intent === "BUG_REPORT" ? ARENA_SUPPORT_AGENT_BUG_REPORT_RELEVANCE_FLOOR : ARENA_SUPPORT_AGENT_RELEVANCE_FLOOR;
}

/** Why a run was low confidence. */
export const ArenaSupportAgentLowConfidenceReasonSchema = z.enum([
  "BELOW_RELEVANCE_FLOOR",
  "CITATION_MISMATCH",
  "FACT_MISMATCH",
  "NOT_GROUNDED",
  /** The answer mixes in words of another writing system than the user's language. */
  "MIXED_SCRIPT",
  /** The user says an earlier KB answer did not solve their problem and no entry it did not use answers it. */
  "UNRESOLVED_AFTER_ANSWER",
  /** The writer found that only the Cede team can help: a check the entries reserve to the team, or a request to act. */
  "TEAM_NEEDED",
  /** The writer found no entry answering the question, twice: low confidence like any other (rephrase, then team). */
  "NO_KB_ANSWER",
]);
export type ArenaSupportAgentLowConfidenceReason = z.infer<typeof ArenaSupportAgentLowConfidenceReasonSchema>;

export const ArenaSupportAgentCandidateSchema = z.object({
  entryId: z.string(),
  question: z.string(),
  /** 1-based; null when the entry was not returned by that search. */
  vectorRank: z.number().int().nullable(),
  keywordRank: z.number().int().nullable(),
  rrfScore: z.number(),
  /** 0–3; null when the run stopped before reranking. */
  rerankScore: z.number().int().min(0).max(3).nullable(),
});
export type ArenaSupportAgentCandidateDto = z.infer<typeof ArenaSupportAgentCandidateSchema>;

export const ArenaSupportAgentCitationSchema = z.object({ entryId: z.string(), quote: z.string() });

export const ArenaSupportAgentVerificationSchema = z.object({
  citationsMatch: z.boolean(),
  factsMatch: z.boolean(),
  grounded: z.boolean(),
  /** Human-readable reasons for any failed check. */
  issues: z.array(z.string()),
  /** The first draft and why it failed, when this one is the single retry. Absent on runs stored before retries. */
  rejectedDraft: z
    .object({ draft: z.string(), issues: z.array(z.string()) })
    .nullable()
    .default(null),
  /** The first translation and why the checks rejected it, when the posted one is the retry. */
  rejectedTranslation: z
    .object({ draft: z.string(), issues: z.array(z.string()) })
    .nullable()
    .default(null),
});
export type ArenaSupportAgentVerificationDto = z.infer<typeof ArenaSupportAgentVerificationSchema>;

export const ArenaSupportAgentStepLatencySchema = z.object({
  rewriteMs: z.number().nullable(),
  retrievalMs: z.number().nullable(),
  rerankMs: z.number().nullable(),
  generateMs: z.number().nullable(),
  verifyMs: z.number().nullable(),
  /** Null for an English answer, and on runs stored before answers were translated. */
  translateMs: z.number().nullable().default(null),
  totalMs: z.number(),
});

export const ArenaSupportAgentTraceSchema = z.object({
  outcome: ArenaSupportAgentOutcomeSchema,
  /** Set on every FAILURE, and on a SKIPPED run whose pipeline had failed before it could post. */
  failureReason: ArenaSupportAgentFailureReasonSchema.nullable(),
  /** The error message behind `failureReason`; null on runs stored before it was kept. */
  failureDetail: z.string().nullable().default(null),
  lowConfidenceReason: ArenaSupportAgentLowConfidenceReasonSchema.nullable(),
  rewrittenQuery: z.string().nullable(),
  /** BCP-47-ish code detected by the rewrite step, e.g. "en", "fr". */
  language: z.string().nullable(),
  /** Null when the run stopped before the rewrite step. */
  intent: ArenaSupportAgentIntentSchema.nullable(),
  /**
   * Set when the trigger message closed a burst of user messages the rewrite split into several requests: this run
   * answers request `requestIndex` (0-based) of `requestCount`, about `requestSubject`. Null on a single-request run.
   */
  requestIndex: z.number().int().nullable().default(null),
  requestCount: z.number().int().nullable().default(null),
  requestSubject: z.string().nullable().default(null),
  candidates: z.array(ArenaSupportAgentCandidateSchema),
  /** Generated answer, also kept when verification failed so ops can see why. */
  draft: z.string().nullable(),
  citations: z.array(ArenaSupportAgentCitationSchema),
  verification: ArenaSupportAgentVerificationSchema.nullable(),
  latency: ArenaSupportAgentStepLatencySchema,
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
});
export type ArenaSupportAgentTraceDto = z.infer<typeof ArenaSupportAgentTraceSchema>;

export const ArenaSupportAgentRunSchema = ArenaSupportAgentTraceSchema.extend({
  id: z.string(),
  conversationId: z.string(),
  /** The USER message that triggered the run. */
  triggerMessageId: z.string(),
  /** The AI message posted by the run; null in SHADOW or when nothing was posted. */
  postedMessageId: z.string().nullable(),
  topic: ArenaSupportTopicSchema,
  mode: ArenaSupportAgentRunModeSchema,
  /** Model snapshot per step, e.g. { rewrite, rerank, generate, verify, embed }. */
  models: z.record(z.string(), z.string()),
  createdAt: z.number(),
});
export type ArenaSupportAgentRunDto = z.infer<typeof ArenaSupportAgentRunSchema>;

/** Query for `GET /v1/cms/arena-support-agent/runs` — newest first, cursor on run id. */
export const CmsListArenaSupportAgentRunsQuerySchema = z.object({
  conversationId: z.string().min(1).optional(),
  before: z.string().min(1).optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
});

export const CmsArenaSupportAgentRunsResponseSchema = z.object({
  runs: z.array(ArenaSupportAgentRunSchema),
  hasMore: z.boolean(),
});
export type CmsArenaSupportAgentRunsResponse = z.infer<typeof CmsArenaSupportAgentRunsResponseSchema>;

// ─── Playground ──────────────────────────────────────────────────────────────

/** Body for `POST /v1/cms/arena-support-agent/playground`. Runs the pipeline; sends and stores nothing. */
export const CmsArenaSupportAgentPlaygroundRequestSchema = z.object({
  topic: ArenaSupportTopicSchema,
  query: kbText(2000),
  /** Also consider entries with `aiEnabled = false`, so ops can test an entry before enabling it. */
  includeDisabled: z.boolean(),
});
export type CmsArenaSupportAgentPlaygroundRequest = z.infer<typeof CmsArenaSupportAgentPlaygroundRequestSchema>;

export const CmsArenaSupportAgentPlaygroundResponseSchema = z.object({ trace: ArenaSupportAgentTraceSchema });
export type CmsArenaSupportAgentPlaygroundResponse = z.infer<typeof CmsArenaSupportAgentPlaygroundResponseSchema>;

// ─── Golden set + offline eval ───────────────────────────────────────────────

export const ArenaSupportGoldenCaseSourceSchema = z.enum(["PLAYGROUND", "WRONG_FLAG", "MANUAL"]);

/** The reply a golden case expects. `HANDOFF` covers a rephrase request too: offline, the streak is always 0. */
export const ArenaSupportGoldenExpectedPathSchema = z.enum([
  "ANSWER",
  "HANDOFF",
  "BUG_REPORT",
  "FEEDBACK",
  "GREETING",
  "THANKS",
]);
export type ArenaSupportGoldenExpectedPath = z.infer<typeof ArenaSupportGoldenExpectedPathSchema>;

export const ArenaSupportGoldenCaseSchema = z.object({
  id: z.string(),
  topic: ArenaSupportTopicSchema,
  query: z.string(),
  /** Entries a correct retrieval must surface; set only when `expectedPath` is `ANSWER`. */
  expectedEntryIds: z.array(z.string()),
  expectedPath: ArenaSupportGoldenExpectedPathSchema,
  source: ArenaSupportGoldenCaseSourceSchema,
  createdBy: z.string().nullable(),
  createdAt: z.number(),
});
export type ArenaSupportGoldenCaseDto = z.infer<typeof ArenaSupportGoldenCaseSchema>;

/** Body for `POST /v1/cms/arena-support-agent/golden/create`. */
export const CmsCreateArenaSupportGoldenCaseRequestSchema = z
  .object({
    topic: ArenaSupportTopicSchema,
    query: kbText(2000),
    expectedEntryIds: z.array(z.string().min(1)).max(ARENA_SUPPORT_GOLDEN_EXPECTED_ENTRIES_MAX),
    expectedPath: ArenaSupportGoldenExpectedPathSchema,
    source: ArenaSupportGoldenCaseSourceSchema,
  })
  .refine((value) => (value.expectedPath === "ANSWER") === value.expectedEntryIds.length > 0, {
    message: "An ANSWER case lists the entries it expects; any other path lists none",
  });
export type CmsCreateArenaSupportGoldenCaseRequest = z.infer<typeof CmsCreateArenaSupportGoldenCaseRequestSchema>;

/** Body for `POST /v1/cms/arena-support-agent/golden/delete`. */
export const CmsArenaSupportGoldenCaseIdRequestSchema = z.object({ id: z.string().min(1) });

export const CmsArenaSupportGoldenCasesResponseSchema = z.object({ cases: z.array(ArenaSupportGoldenCaseSchema) });
export type CmsArenaSupportGoldenCasesResponse = z.infer<typeof CmsArenaSupportGoldenCasesResponseSchema>;

export const ArenaSupportEvalRunStatusSchema = z.enum(["RUNNING", "DONE", "FAILED"]);

export const ArenaSupportEvalMetricsSchema = z.object({
  caseCount: z.number().int(),
  /** ANSWER cases whose expected entries are not all in the KB with aiEnabled + READY; left out of recall@5 and MRR. */
  staleCaseCount: z.number().int(),
  /** Share of non-stale ANSWER cases with ≥ 1 expected entry in the top 5 after rerank. */
  recallAt5: z.number(),
  /** Mean of 1/rank of the first expected entry (0 when absent) over non-stale ANSWER cases. */
  mrr: z.number(),
  /** Share of cases whose reply path matches `expectedPath`. */
  pathAccuracy: z.number(),
  /**
   * Share of cases whose detected intent leads to `expectedPath`: ANSWER accepts QUESTION or BUG_REPORT, HANDOFF
   * accepts QUESTION, BUG_REPORT / FEEDBACK / GREETING / THANKS their own intent. No detected intent is a miss.
   */
  intentAccuracy: z.number(),
  /** Share of drafted answers whose verification passed; an empty answer (the model declined) is not counted. */
  faithfulness: z.number(),
});
export type ArenaSupportEvalMetricsDto = z.infer<typeof ArenaSupportEvalMetricsSchema>;

export const ArenaSupportEvalCaseResultSchema = z.object({
  caseId: z.string(),
  query: z.string(),
  passed: z.boolean(),
  /** An expected entry was deleted, AI-disabled or not READY at run time: the case needs updating, not the agent. */
  stale: z.boolean(),
  firstExpectedRank: z.number().int().nullable(),
  trace: ArenaSupportAgentTraceSchema,
});
export type ArenaSupportEvalCaseResultDto = z.infer<typeof ArenaSupportEvalCaseResultSchema>;

/** An eval run without its per-case results, which only `GET .../eval/runs/:id` returns. */
export const ArenaSupportEvalRunSummarySchema = z.object({
  id: z.string(),
  status: ArenaSupportEvalRunStatusSchema,
  /** Null while RUNNING or when FAILED. */
  metrics: ArenaSupportEvalMetricsSchema.nullable(),
  error: z.string().nullable(),
  models: z.record(z.string(), z.string()),
  startedBy: z.string().nullable(),
  createdAt: z.number(),
  finishedAt: z.number().nullable(),
});
export type ArenaSupportEvalRunSummaryDto = z.infer<typeof ArenaSupportEvalRunSummarySchema>;

/** Response of `GET /v1/cms/arena-support-agent/eval/runs/:id`. */
export const ArenaSupportEvalRunSchema = ArenaSupportEvalRunSummarySchema.extend({
  results: z.array(ArenaSupportEvalCaseResultSchema),
});
export type ArenaSupportEvalRunDto = z.infer<typeof ArenaSupportEvalRunSchema>;

/** Response of `GET /v1/cms/arena-support-agent/eval/runs`. */
export const CmsArenaSupportEvalRunsResponseSchema = z.object({ runs: z.array(ArenaSupportEvalRunSummarySchema) });
export type CmsArenaSupportEvalRunsResponse = z.infer<typeof CmsArenaSupportEvalRunsResponseSchema>;

/** Response of `POST /v1/cms/arena-support-agent/eval/run` — the run executes in a BullMQ job. */
export const CmsStartArenaSupportEvalRunResponseSchema = z.object({ evalRunId: z.string() });
export type CmsStartArenaSupportEvalRunResponse = z.infer<typeof CmsStartArenaSupportEvalRunResponseSchema>;

// ─── Support AI dashboard ────────────────────────────────────────────────────

/** Query for `GET /v1/cms/arena-support-agent/dashboard`. Window in whole days ending now. */
export const CmsArenaSupportAgentDashboardQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90),
});

const durationStats = z.object({ p50Ms: z.number().nullable(), p95Ms: z.number().nullable(), count: z.number().int() });
const csatStats = z.object({ average: z.number().nullable(), count: z.number().int() });

export const CmsArenaSupportAgentDashboardResponseSchema = z.object({
  /** User's first message → first non-user message (AI or ops). */
  ttfr: durationStats,
  /** User's first message → first AI ANSWER or ops message (acks and rephrase requests excluded). */
  ttfur: durationStats,
  resolvedConversations: z.number().int(),
  /** Resolved with no ops message / resolved. Null when nothing resolved. */
  resolvedWithoutOpsRate: z.number().nullable(),
  runs: z.number().int(),
  /**
   * LIVE requests: LIVE runs other than SKIPPED, GREETING, THANKS and FEEDBACK. The base of `handoffRate` and
   * `rephraseRate`; SHADOW runs post nothing.
   */
  liveRequests: z.number().int(),
  outcomes: z.record(ArenaSupportAgentOutcomeSchema, z.number().int()),
  /** Failure reasons that occurred; SKIPPED runs keep theirs. */
  failureReasons: z.partialRecord(ArenaSupportAgentFailureReasonSchema, z.number().int()),
  /** (LOW_CONFIDENCE_HANDOFF + FAILURE) / `liveRequests`: what the agent could not answer. Null without requests. */
  handoffRate: z.number().nullable(),
  /** LOW_CONFIDENCE_REPHRASE / `liveRequests`. Null without requests. */
  rephraseRate: z.number().nullable(),
  aiMessages: z.number().int(),
  reviewedAiMessages: z.number().int(),
  wrongAiMessages: z.number().int(),
  /** WRONG / reviewed. Null when nothing reviewed. */
  wrongAnswerRate: z.number().nullable(),
  csat: z.object({
    aiOnly: csatStats,
    humanOnly: csatStats,
    mixed: csatStats,
    /** No AI ANSWER and no ops reply: the AI only sent fixed copy (rephrase, handoff ack, greeting, bug ack), or nothing. */
    noAnswer: csatStats,
  }),
  latency: durationStats,
  daily: z.array(
    z.object({
      /** YYYY-MM-DD (UTC). */
      date: z.string(),
      runs: z.number().int(),
      /** LIVE runs only, like `handoffRate`. */
      handoffs: z.number().int(),
      /** LIVE runs only. */
      failures: z.number().int(),
      ttfrP50Ms: z.number().nullable(),
    }),
  ),
});
export type CmsArenaSupportAgentDashboardResponse = z.infer<typeof CmsArenaSupportAgentDashboardResponseSchema>;
